/*
 * The campaign recipientsFilter, which is being introduced with
 * API Triggered Broadcasts, uses a new format that is different from
 * what's currently used in anchors/filters. It is no longer a base64-encoded
 * string and the underlying structure is also different.
 * These deserializing functions transform the the new format into
 * the one used by anchors/filters.
 * For example:
 * {
 *   and: [
 *     { segment: { id: 1 } },
 *     { not: { segment: { id: 2 } } }
 *   ]
 * }
 * will be transformed into:
 * [
 *   { negate: false, segments: [1], type: 'segments_negatable' },
 *   { negate: true, segments: [2], type: 'segments_negatable' }
 * ]
 * Ideally this deserializer won't be needed in it's current form,
 * but it's easier to do this rather than refactoring components like
 * condition-list to accept both formats.
 * In future, when we do abandon the old format, we might want to make this
 * into a similar structure like models/conditions and utils/conditions,
 * but that's overkill for now.
 */
import { isEmpty } from '@ember/utils';
import { MESSAGE_TYPE, TYPE_MAP } from 'cio/utils/named-bitmap-filters';

function segmentFilterToCondition(segment, not = false) {
  return {
    negate: not,
    type: 'segments_negatable',
    segments: [segment.id],
  };
}

function attributeFilterToCondition(attribute, not = false) {
  let operator = `${not ? '!' : ''}${attribute.operator}`;
  let condition = {
    field: attribute.field,
    operator,
    type: 'attribute',
  };

  if (attribute.value) {
    condition.value = attribute.value;
  }

  return condition;
}

// message filters are represented as "named_bitmap" in the backend. They take
// the following format:
// {
//   "named_bitmap": {
//     "type": 3,
//     "name": "se", // this is base64-encoded
//     "inverse": false
//   }
// }
// where type is always 3 (representing an event),
// and name is one of
// https://github.com/customerio/services/blob/master/src/customerio/core/events/types/shorten.go#L89
// If the message filter refers to a specific message ID or a non-email action
// type, the `name` will be, for example:
//   email action, all emails: `se`, `be` etc
//   email action, specific email: `se-output_1` (the message id - "output_1" is
//   appended after a dash -)
//   non-email action, all of type: `sa-push`, `cla-webhook` (the type is
//     appended after a dash -)
//   non-email action, specific message: `sa-push_1`, `cla-webhook_1`
function namedBitmapMessageFilterToCondition(event, not = false) {
  let { name } = event;
  let messageType = atob(name);
  let messageName;

  let dashIndex = messageType.indexOf('-');
  // if there is a dash in the name, the event refers to a specific action type
  // or ID
  if (dashIndex > -1) {
    messageName = messageType.substring(dashIndex + 1, messageType.length);
    messageType = messageType.substring(0, dashIndex);

    // if there is no underscore in the message name (id), then the filter refers
    // to all messages of that type. If it's a non-email action, we'll need to add
    // an asterisk (*) to map correctly to the UI filters format.
    if (!messageName.includes('_') && !['output', 'newsletter'].includes(messageName)) {
      messageName = `${messageName}_*`;
    }
  } else {
    // if there is no dash, then the event refers to all EMAIL actions. We need
    // to use an asterisk for the messageName in order for the UI to work
    // properly
    messageName = '*';
  }

  // now we need to map the message type from what the backend gives us to a
  // format recognized by the UI
  let [uiEventType] = Object.entries(TYPE_MAP).find(([_key, value]) => value === messageType);

  return {
    event: {
      type: uiEventType,
      name: messageName,
      filters: {},
    },
    times: 1,
    within: 0,
    inverse: not,
    type: 'message',
  };
}

// TODO: Figure out how to handle mixed OR conditions (segments and attributes)
/* The "old" format doesn't quite support this, since ORing only segments
 * looks like this:
 * { "negate": false, "segments": [1, 2, 3], "type": "segments_negatable" }
 * so there's no space for mixing attributes in.
 * Only handling segments in here is fine for now, since there is no place
 * in the UI where such OR conditions would be created.
 */
function orFilterToCondition(filter, not = false) {
  let segments = filter.map((c) => c.segment.id);
  return {
    negate: not,
    type: 'segments_negatable',
    segments,
  };
}

function notFilterToCondition(filter) {
  return deserialize(filter, true);
}

function andFilterToCondition(filter, not) {
  return filter.map((f) => deserialize(f, not));
}

export default function deserialize(filter, not = false) {
  if (isEmpty(filter)) {
    return [];
  }
  if (filter.segment) {
    return segmentFilterToCondition(filter.segment, not);
  }
  if (filter.attribute) {
    return attributeFilterToCondition(filter.attribute, not);
  }
  if (filter.DeviceAttribute || filter.device_attribute) {
    let data = filter.DeviceAttribute ?? filter.device_attribute;
    let condition = attributeFilterToCondition(data, not);
    condition.type = 'device_attribute';
    return condition;
  }
  if (filter.named_bitmap && filter.named_bitmap.type === MESSAGE_TYPE) {
    // message conditions
    return namedBitmapMessageFilterToCondition(filter.named_bitmap, not);
  }
  if (filter.or) {
    return orFilterToCondition(filter.or, not);
  }
  if (filter.not) {
    return notFilterToCondition(filter.not);
  }
  if (filter.and) {
    return andFilterToCondition(filter.and, not);
  }
  return [];
}
